import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

import Sidebar from "../../components/sidebar/Sidebar";
import SectionHeader from "../../components/sectionHeader/SectionHeader";
import MainSection from "../../components/mainLayout/Main";

import { getOneEmployee, editEmployee } from "../../redux/actions/authActions";

function EditEmployee() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();

	const employee = useSelector((state) => state.auth.employee);

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phoneNumber: "",
		department: "",
		password: "",
	});
	const [showPassword, setShowPassword] = useState(false);

	useEffect(() => {
		if (id) {
			dispatch(getOneEmployee(id));
		}
	}, [dispatch, id]);

	useEffect(() => {
		if (employee) {
			setFormData({
				name: employee.name || "",
				email: employee.email || "",
				phoneNumber: employee.phoneNumber || "",
				department: employee.department || "",
				password: "",
			});
		}
	}, [employee]);

	const handleChange = (e) => {
		const { id, value } = e.target;
		if (id === "phoneNumber") {
			const numeric = value.replace(/\D/g, "").slice(0, 10);
			setFormData({ ...formData, phoneNumber: numeric });
		} else if (id === "name") {
			const cleanName = value.replace(/[^a-zA-Z\s]/g, "");
			setFormData({ ...formData, name: cleanName });
		} else {
			setFormData({ ...formData, [id]: value });
		}
	};

	const handleSetDefaultPassword = () => {
		const cleanName = (formData.name || "").replace(/\s+/g, "");
		const lastDigits = formData.phoneNumber && formData.phoneNumber.length >= 5 ? formData.phoneNumber.slice(-5) : (formData.phoneNumber || "");
		setFormData((prev) => ({
			...prev,
			password: cleanName + lastDigits,
		}));
		setShowPassword(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const payload = { ...formData };
			if (!payload.password || payload.password.trim() === "") {
				delete payload.password;
			} else {
				payload.password = payload.password.trim();
			}
			await dispatch(editEmployee(payload, id));
			navigate("/team-management");
		} catch (error) {
			console.error("Error editing employee:", error);
		}
	};

	return (
		<div className="flex bg-gray-100 min-h-screen">
			<Sidebar />
			<MainSection>
				<div className="container mx-auto px-4 py-8">
					<SectionHeader title="Edit Employee" />

					<div className="bg-white rounded-xl shadow-lg p-8 mt-6">
						<form onSubmit={handleSubmit} className="space-y-6">
							{/* Employee Name */}
							<div>
								<label
									htmlFor="name"
									className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
								>
									Employee Name
								</label>
								<input
									type="text"
									id="name"
									value={formData.name}
									onChange={handleChange}
									className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
									placeholder="Enter employee name"
									required
								/>
							</div>

							{/* Email (read-only) */}
							<div>
								<label
									htmlFor="email"
									className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
								>
									Email Address
								</label>
								<input
									type="email"
									id="email"
									value={formData.email}
									onChange={handleChange}
									readOnly
									className="bg-gray-200 border border-gray-300 text-gray-500 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 cursor-not-allowed"
									placeholder="Enter email address"
									required
								/>
							</div>

							{/* Phone Number */}
							<div>
								<label
									htmlFor="phoneNumber"
									className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
								>
									Phone Number
								</label>
								<input
									type="text"
									id="phoneNumber"
									value={formData.phoneNumber}
									onChange={handleChange}
									className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
									placeholder="Enter phone number"
									required
								/>
							</div>

							{/* Department Dropdown */}
							<div>
								<label
									htmlFor="department"
									className="block mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
								>
									Department
								</label>
								<select
									id="department"
									value={formData.department}
									onChange={handleChange}
									className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 transition-colors"
								>
									<option value="">Select department</option>
									<option value="HR">HR</option>
									<option value="Engineering">Engineering</option>
									<option value="Marketing">Marketing</option>
									<option value="Sales">Sales</option>
								</select>
							</div>

							{/* Password */}
							<div>
								<div className="flex justify-between items-center mb-2">
									<label
										htmlFor="password"
										className="text-xs font-semibold text-gray-500 uppercase tracking-wide"
									>
										Change Password
									</label>
									<button
										type="button"
										onClick={handleSetDefaultPassword}
										className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
									>
										Use Default Password
									</button>
								</div>
								<div className="relative">
									<input
										type={showPassword ? "text" : "password"}
										id="password"
										value={formData.password}
										onChange={handleChange}
										className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10 transition-colors font-mono"
										placeholder="Leave blank to keep current password"
									/>
									<button
										type="button"
										onClick={() => setShowPassword(!showPassword)}
										className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
										title={showPassword ? "Hide password" : "Show password"}
									>
										{showPassword ? <AiOutlineEyeInvisible size={18} /> : <AiOutlineEye size={18} />}
									</button>
								</div>
								<p className="mt-1 text-[11px] text-gray-500">
									Leave blank to keep current password, or enter a new password / click "Use Default Password" to reset.
								</p>
							</div>

							{/* Submit Button */}
							<div className="pt-4">
								<button
									type="submit"
									className="w-full flex justify-center py-2 px-4 rounded-full shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
								>
									Save Changes
								</button>
							</div>
						</form>
					</div>
				</div>
			</MainSection>
		</div>
	);
}

export default EditEmployee;
